import { applyThemeToWidgetUrl } from './applyThemeToWidgetUrl';
import { getUserTheme, normalizeTheme } from './theme';

const PLACEHOLDER_KEYS = [
  'broadageEventId',
  'sportId',
  'type',
  'broadageTeamId',
  'broadageLeagueId',
  'teamInfo',
  'roundNavigationRule',
  'theme',
  'homeTeamId',
  'awayTeamId',
];

const WEBVIEW_QUERY_KEYS = ['webview', 'rendertype', 'channel'];

export const BROADAGE_REDIRECT_CMS_KEYS = {
  soccerRedirectUrl: 'league_center_livescore_soccer_url',
  basketballRedirectUrl: 'league_center_livescore_basketball_url',
  handballRedirectUrl: 'league_center_livescore_handball_url',
  iceHockeyRedirectUrl: 'league_center_livescore_icehockey_url',
  volleyballRedirectUrl: 'league_center_livescore_volleyball_url',
  tableTennisRedirectUrl: 'league_center_livescore_tabletennis_url',
  teamRedirectUrl: 'league_center_team_url',
  matchRedirectUrl: 'league_center_match_url',
};

const REDIRECT_QUERY_KEYS = Object.keys(BROADAGE_REDIRECT_CMS_KEYS);
const SPORT_PLACEHOLDER = '%@';
const SPORT_PLACEHOLDER_TOKEN = '__BROADAGE_SPORT__';

const toParamValue = value => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const normalizeType = type => (type || '').replace(/\s/g, '');

const resolveTheme = theme => {
  if (theme) return normalizeTheme(theme);
  return getUserTheme();
};

const appendQueryParam = (url, key, value) => {
  if (value === undefined || value === null || value === '') return url;
  if (url.includes(`${key}=`)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${key}=${encodeURIComponent(value)}`;
};

const withSportPlaceholderToken = (url, fn) => {
  const tokenized = String(url).split(SPORT_PLACEHOLDER).join(SPORT_PLACEHOLDER_TOKEN);
  const next = fn(tokenized);
  return String(next).split(SPORT_PLACEHOLDER_TOKEN).join(SPORT_PLACEHOLDER);
};

export const toPageOriginUrl = (url, origin) => {
  if (!url || !origin) return url;
  try {
    return withSportPlaceholderToken(url, tokenized => {
      const parsed = new URL(tokenized, origin);
      const page = new URL(origin);
      parsed.protocol = page.protocol;
      parsed.host = page.host;
      return parsed.toString();
    });
  } catch (err) {
    return url;
  }
};

const copyWebviewParams = (url, search) => {
  if (!url || !search) return url;
  try {
    return withSportPlaceholderToken(url, tokenized => {
      const parsed = new URL(tokenized);
      const currentParams = new URLSearchParams(
        typeof search === 'string' && search.startsWith('?') ? search.slice(1) : search
      );
      WEBVIEW_QUERY_KEYS.forEach(key => {
        const value = currentParams.get(key);
        if (value && !parsed.searchParams.get(key)) {
          parsed.searchParams.set(key, value);
        }
      });
      return parsed.toString();
    });
  } catch (err) {
    return url;
  }
};

export const resolveBroadageRedirectParams = ({
  getMessageText,
  origin,
  search,
} = {}) => {
  if (typeof getMessageText !== 'function') return {};

  const params = {};
  Object.entries(BROADAGE_REDIRECT_CMS_KEYS).forEach(([param, cmsKey]) => {
    const raw = getMessageText({ key: cmsKey });
    if (!raw) return;
    params[param] = copyWebviewParams(toPageOriginUrl(raw, origin), search);
  });
  return params;
};

/**
 * Builds Broadage micro-app iframe URL from CMS template.
 * @param {{ template: string, params?: object }} args
 * @returns {string|null}
 */
export const buildBroadageWidgetUrl = ({ template, params = {} } = {}) => {
  if (!template || typeof template !== 'string') return null;

  const sportId = params.sportId ?? params.sportType;
  const values = {
    broadageEventId: params.broadageEventId,
    sportId,
    type: normalizeType(params.type),
    broadageTeamId: params.broadageTeamId,
    broadageLeagueId: params.broadageLeagueId,
    teamInfo: params.teamInfo,
    roundNavigationRule: params.roundNavigationRule,
    theme: resolveTheme(params.theme),
    homeTeamId: params.homeTeamId,
    awayTeamId: params.awayTeamId,
  };

  let url = template;
  PLACEHOLDER_KEYS.forEach(key => {
    const placeholder = `{${key}}`;
    if (url.includes(placeholder)) {
      url = url.split(placeholder).join(encodeURIComponent(toParamValue(values[key])));
    }
  });

  // Append params that may come from BE/call-sites but are not in the CMS template.
  ['homeTeamId', 'awayTeamId', 'dataListHeight'].forEach(key => {
    const value = key === 'dataListHeight' ? params.dataListHeight : values[key];
    if (!template.includes(`{${key}}`) && value !== undefined && value !== null) {
      url = appendQueryParam(url, key, toParamValue(value));
    }
  });

  REDIRECT_QUERY_KEYS.forEach(key => {
    url = appendQueryParam(url, key, toParamValue(params[key]));
  });

  if (typeof window !== 'undefined' && window.location?.search) {
    const currentParams = new URLSearchParams(window.location.search);
    WEBVIEW_QUERY_KEYS.forEach(key => {
      const value = currentParams.get(key);
      if (value) {
        url = appendQueryParam(url, key, value);
      }
    });
  }

  return applyThemeToWidgetUrl(url, values.theme);
};

export default buildBroadageWidgetUrl;
